import {
    type TextInputProps,
    type TextStyle,
    type ViewStyle,
    StyleSheet,
    Platform
} from "react-native";
import {
    type InputOTPCharactersConstantType,
    type InputOTPFieldTypeConstantType,
    type InputOTPAutofillConstantType,
    type InputOTPSizeKeyConstantType,
    type InputOTPColorsConstantType,
    type InputOTPChangeConstantType,
    type InputOTPItemsConstantType,
    type InputOTPDynamicStyleType,
    type InputOTPKeyboardRole,
    type InputOTPCharacterSet,
    type InputOTPSizeRole,
    type InputOTPChange,
    type InputOTPColors,
    type InputOTPItem,
    type InputOTPSize,
    type InputOTPType
} from "./type";
import type {
    Mutable
} from "../../types";
import type {
    FieldType
} from "../fieldControl/type";
import {
    getElevationStyle
} from "../../helpers/theme/elevation";
import {
    getFieldFocusStyle
} from "../../helpers/theme/state";
import {
    getInputTextStyle
} from "../../helpers/theme/input";
import {
    concentric
} from "../../helpers/theme/concentric";
import {
    getCornerStyle
} from "../../helpers/theme/corner";
import {
    getFieldColors
} from "../fieldControl/stylesheet";
import {
    webStyle
} from "../../utils";

export const INPUT_OTP_SIZES: Record<InputOTPSize, InputOTPSizeRole> = {
    large: {
        variant: "headlineSmallSize",
        height: "large"
    },
    medium: {
        variant: "titleLargeSize",
        height: "medium"
    }
};

export const INPUT_OTP_FIELD_TYPES: Record<InputOTPType, FieldType> = {
    primary: "neutral",
    success: "success",
    warning: "warning",
    danger: "danger",
    info: "info"
};

export const INPUT_OTP_KEYBOARDS: Record<InputOTPCharacterSet, InputOTPKeyboardRole> = {
    numeric: {
        keyboardType: "number-pad",
        inputMode: "numeric"
    },
    alphanumeric: {
        keyboardType: "default",
        inputMode: "text"
    }
};

export const INPUT_OTP_CHARACTER_PATTERNS: Record<InputOTPCharacterSet, RegExp> = {
    alphanumeric: /[A-Za-z0-9]/,
    numeric: /[0-9]/
};

export const INPUT_OTP_LOCALIZED_DIGITS = /[٠-٩۰-۹]/g;

export const INPUT_OTP_DIGIT_MODULO = 16;

const getFirstDifferenceIndex = (first: string, second: string): number => {
    let index = 0;

    while(index < first.length && index < second.length && first.charAt(index) === second.charAt(index)) {
        index += 1;
    }

    return index;
};

export const getInputOTPFieldType = ({
    type
}: InputOTPFieldTypeConstantType): FieldType => {
    return INPUT_OTP_FIELD_TYPES[type];
};

export const getInputOTPSizeKey = ({
    pointerType,
    size
}: InputOTPSizeKeyConstantType): InputOTPSize => {
    if(size) {
        return size;
    }

    return pointerType === "coarse" ? "large" : "medium";
};

export const getInputOTPCharacters = ({
    characterSet,
    text
}: InputOTPCharactersConstantType): string => {
    const pattern = INPUT_OTP_CHARACTER_PATTERNS[characterSet];

    return Array.from(text.replace(INPUT_OTP_LOCALIZED_DIGITS, (digit) => String(digit.charCodeAt(0) % INPUT_OTP_DIGIT_MODULO)))
        .filter((character) => pattern.test(character))
        .join("");
};

export const getInputOTPItems = ({
    groupSize,
    length
}: InputOTPItemsConstantType): Array<InputOTPItem> => {
    const items: Array<InputOTPItem> = [];

    for(let index = 0; index < length; index += 1) {
        if(groupSize && groupSize < length && index > 0 && index % groupSize === 0) {
            items.push({
                kind: "separator",
                index
            });
        }

        items.push({
            kind: "slot",
            index
        });
    }

    return items;
};

export const getInputOTPAutofill = ({
    platform,
    isMasked
}: InputOTPAutofillConstantType): Pick<TextInputProps, "autoComplete" | "textContentType"> => {
    if(isMasked) {
        return {
            autoComplete: "off"
        };
    }

    if(platform === "ios") {
        return {
            textContentType: "oneTimeCode"
        };
    }

    if(platform === "android") {
        return {
            autoComplete: "sms-otp"
        };
    }

    return {
        autoComplete: "one-time-code"
    };
};

export const getInputOTPChange = ({
    isReplaceOnFirstInput,
    previousValue,
    characterSet,
    cursor,
    length,
    text
}: InputOTPChangeConstantType): InputOTPChange => {
    const difference = text.length - previousValue.length;

    const index = Math.min(cursor, previousValue.length);

    if(difference === 1) {
        const isAtCursor = `${text.slice(0, index)}${text.slice(index + 1)}` === previousValue;

        const insertIndex = isAtCursor ? index : getFirstDifferenceIndex(previousValue, text);

        const character = getInputOTPCharacters({
            text: text.charAt(insertIndex),
            characterSet
        });

        if(!character) {
            return {
                cursor: insertIndex,
                value: previousValue
            };
        }

        if(isReplaceOnFirstInput) {
            return {
                value: character,
                cursor: 1
            };
        }

        const target = Math.min(insertIndex, length - 1);

        return {
            value: `${previousValue.slice(0, target)}${character}${previousValue.slice(target + 1)}`.slice(0, length),
            cursor: target + 1
        };
    }

    if(difference === -1) {
        if(index < previousValue.length && `${previousValue.slice(0, index)}${previousValue.slice(index + 1)}` === text) {
            return {
                cursor: index,
                value: text
            };
        }

        if(index > 0 && `${previousValue.slice(0, index - 1)}${previousValue.slice(index)}` === text) {
            return {
                cursor: index - 1,
                value: text
            };
        }

        return {
            cursor: getFirstDifferenceIndex(previousValue, text),
            value: text
        };
    }

    if(difference > 1 && `${text.slice(0, index)}${text.slice(index + difference)}` === previousValue) {
        const pastedValue = getInputOTPCharacters({
            text: text.slice(index, index + difference),
            characterSet
        }).slice(0, length);

        if(!pastedValue) {
            return {
                value: previousValue,
                cursor: index
            };
        }

        return {
            cursor: pastedValue.length,
            value: pastedValue
        };
    }

    const value = getInputOTPCharacters({
        characterSet,
        text
    }).slice(0, length);

    return {
        cursor: value.length,
        value
    };
};

export const getInputOTPColors = ({
    isDisabled,
    isLoading,
    colors,
    type
}: InputOTPColorsConstantType): InputOTPColors => {
    const fieldColors = getFieldColors({
        type: INPUT_OTP_FIELD_TYPES[type],
        isReadOnly: isLoading,
        isDisabled,
        colors
    });

    if(isDisabled) {
        return {
            ...fieldColors,
            separatorColor: colors.system.state.content.disabled.neutral
        };
    }

    return {
        ...fieldColors,
        valueColor: isLoading ? colors.content.text.mid : fieldColors.valueColor,
        separatorColor: colors.content.icon.low
    };
};

const INPUT_OTP_MIN_SLOT_RATIO = 0.6;

const stylesheet = StyleSheet.create({
    input: {
        ...StyleSheet.absoluteFill,
        backgroundColor: "transparent",
        pointerEvents: "none",
        color: "transparent",
        borderWidth: 0,
        padding: 0,
        margin: 0,
        ...Platform.select({
            default: {
                direction: "ltr"
            },
            web: {
            }
        })
    },
    flash: {
        ...StyleSheet.absoluteFill,
        pointerEvents: "none"
    },
    slot: {
        justifyContent: "center",
        alignItems: "center",
        borderStyle: "solid",
        flexShrink: 1,
        flexGrow: 0,
        ...webStyle({
            cursor: "text"
        })
    },
    hintText: {
        alignItems: "flex-start",
        flexDirection: "row"
    },
    disabledSlot: {
        ...webStyle({
            cursor: "not-allowed"
        })
    },
    separator: {
        justifyContent: "center",
        alignItems: "center"
    },
    group: {
        alignSelf: "flex-start",
        flexDirection: "row",
        alignItems: "center",
        position: "relative",
        maxWidth: "100%",
        ...Platform.select({
            default: {
                direction: "ltr"
            },
            web: {
            }
        })
    },
    hintTextIcon: {
        justifyContent: "center"
    },
    container: {
        flexDirection: "column",
        alignSelf: "flex-start",
        maxWidth: "100%"
    },
    labelContainer: {
        alignItems: "baseline",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    loadingSlot: {
        ...webStyle({
            cursor: "progress"
        })
    },
    slotText: {
        fontVariant: [
            "tabular-nums"
        ]
    },
    hintTextContent: {
        flexShrink: 1
    }
});

export const useStyles = ({
    isFocusedSlotVisible,
    controlHeights,
    currentColors,
    shapeConfigs,
    touchTargets,
    pointerType,
    shadowAlpha,
    elevations,
    isDisabled,
    typography,
    iconSizes,
    isHovered,
    isLoading,
    radiuses,
    borders,
    colors,
    spaces,
    size,
    type
}: InputOTPDynamicStyleType) => {
    const sizeRole = INPUT_OTP_SIZES[size];

    const cellSize = controlHeights[sizeRole.height];

    const slotTypography = typography[sizeRole.variant];

    const styles = {
        group: {
            minHeight: pointerType === "coarse" ? Math.max(cellSize, touchTargets.coarse) : cellSize,
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>,
        slot: {
            minWidth: Math.round(cellSize * INPUT_OTP_MIN_SLOT_RATIO),
            backgroundColor: currentColors.containerColor,
            borderColor: currentColors.borderColor,
            borderWidth: borders.line,
            ...getCornerStyle({
                radius: radiuses.form,
                shapeConfigs
            }),
            height: cellSize,
            width: cellSize
        } as Mutable<ViewStyle>,
        flash: {
            backgroundColor: colors.content.container.selected,
            ...getCornerStyle({
                radius: concentric({
                    outer: radiuses.form,
                    inset: borders.line,
                    role: radiuses.form
                }),
                shapeConfigs
            })
        } as Mutable<ViewStyle>,
        hintTextIcon: {
            height: typography.bodyMediumSize.lineHeight
        } as Mutable<ViewStyle>,
        dot: {
            backgroundColor: currentColors.valueColor,
            height: iconSizes.small / 2,
            width: iconSizes.small / 2,
            borderRadius: radiuses.full
        } as Mutable<ViewStyle>,
        caret: {
            backgroundColor: colors.content.text.high,
            height: slotTypography.fontSize,
            width: borders.focus
        } as Mutable<ViewStyle>,
        activeSlot: {} as Mutable<ViewStyle>,
        input: {
            ...getInputTextStyle({
                textStyle: slotTypography,
                pointerType,
                typography
            })
        } as Mutable<TextStyle>,
        hintText: {
            gap: spaces.spacingXs
        },
        container: {
            gap: spaces.spacingSm
        }
    };

    if(!isDisabled && !isLoading) {
        Object.assign(styles.slot, getElevationStyle({
            shadowColor: colors.system.shadow,
            level: "level1",
            shadowAlpha,
            elevations
        }));
    }

    if(isHovered && !isFocusedSlotVisible && !isDisabled && !isLoading && currentColors.borderColor === colors.content.border.default) {
        styles.slot.borderColor = colors.content.border.hover;
    }

    if(!isDisabled && !isLoading) {
        Object.assign(styles.activeSlot, getFieldFocusStyle({
            isError: type === "danger",
            isFocused: true,
            borders,
            colors
        }));

        if(type === "success" || type === "warning") {
            styles.activeSlot.borderColor = currentColors.borderColor;
        }
    }

    return styles;
};
export default stylesheet;
